/**
 * EditMemberModal - Modal para editar função/role de um membro
 * Responsabilidade: Alterar a role de um membro da organização
 * 
 * Princípios: Componentização, Single Responsibility
 */
import React, { useState, useEffect } from 'react';
import Modal from '../../../components/ui/Modal';
import { Button } from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';

type Role = 'admin' | 'member';

interface EditMemberModalProps {
    isOpen: boolean;
    memberName: string;
    memberEmail: string;
    currentRole: Role | 'viewer' | 'owner';
    onSave: (newRole: Role) => Promise<void>;
    onClose: () => void;
}

const ROLE_OPTIONS = [
    { value: 'admin', label: 'Administrador' },
    { value: 'member', label: 'Membro' },
];

const EditMemberModal: React.FC<EditMemberModalProps> = ({
    isOpen,
    memberName,
    memberEmail,
    currentRole,
    onSave,
    onClose
}) => {
    const initialRole: Role = currentRole === 'admin' ? 'admin' : 'member';
    const [selectedRole, setSelectedRole] = useState<Role>(initialRole);
    const [isSaving, setIsSaving] = useState(false);

    // Reset quando modal abre com novo membro
    useEffect(() => {
        if (isOpen) {
            setSelectedRole(currentRole === 'admin' ? 'admin' : 'member');
        }
    }, [isOpen, currentRole]);

    const handleSave = async () => {
        const baseline: Role = currentRole === 'admin' ? 'admin' : 'member';
        if (selectedRole === baseline && currentRole !== 'viewer') {
            onClose();
            return;
        }

        setIsSaving(true);
        try {
            await onSave(selectedRole);
            onClose();
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} size="sm">
            <Modal.Header onClose={onClose}>
                Editar Membro
            </Modal.Header>

            <Modal.Body>
                <div className="mb-4 p-3 bg-surface-subtle rounded-lg border border-subtle">
                    <p className="font-semibold text-default">{memberName || 'Sem nome'}</p>
                    <p className="text-sm text-muted">{memberEmail}</p>
                </div>

                <Select
                    label="Função"
                    value={selectedRole}
                    onChange={(e) => setSelectedRole(e.target.value as Role)}
                    options={ROLE_OPTIONS}
                />

                <p className="mt-3 text-xs text-muted">
                    <strong>Administrador:</strong> vê todas as conversas, tarefas e negociações.<br />
                    <strong>Membro:</strong> vê apenas o que estiver atribuído a ele.
                </p>
            </Modal.Body>

            <Modal.Footer>
                <Button variant="secondary" onClick={onClose} disabled={isSaving}>
                    Cancelar
                </Button>
                <Button variant="default" onClick={handleSave} disabled={isSaving}>
                    {isSaving ? 'Salvando...' : 'Salvar'}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default EditMemberModal;
